import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import List from "../../../components/List/List"
import { EPlatformNoticeAudienceType } from "../../../Enum/EPlatformNoticeAudienceType"
import { useConfirmation } from "../../../context/useConfirmationContext"
import { usePlatformNotices } from "../../../hooks/PlatformNotice/usePlatformNotices"
import { PlatformNotice } from "../../../Objects/PlatformNotice/PlatformNotice"
import { PlatformNoticeRecipient } from "../../../Objects/PlatformNotice/PlatformNoticeRecipient"
import { AdminListCard, AdminListMetricItem } from "../components/AdminListCard"
import { PlatformNoticePopup } from "../PlatformNoticePopup"

const formatDateTime = (date?: Date) =>
  date
    ? new Intl.DateTimeFormat("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      }).format(date)
    : "-"

const selectedAudienceTypes = new Set<EPlatformNoticeAudienceType>([
  EPlatformNoticeAudienceType.SelectedRestaurants,
  EPlatformNoticeAudienceType.SelectedCouriers,
  EPlatformNoticeAudienceType.SelectedResellers,
])

export const PlatformNoticesSection: React.FC = () => {
  const { t } = useTranslation("platformNotice")

  const { setNewConfirmation } = useConfirmation()

  const { getAdminNotices, getRecipients, deleteAdminNotice } = usePlatformNotices()

  const [notices, setNotices] = useState<PlatformNotice[]>([])
  const [recipients, setRecipients] = useState<PlatformNoticeRecipient[]>([])
  const [editingNotice, setEditingNotice] = useState<PlatformNotice | null>(null)

  const hasLoadedInitialData = useRef(false)

  const statusFilters = useMemo(() => [t("status_active"), t("status_inactive"), t("status_all")], [t])

  const refresh = useCallback(() => getAdminNotices(setNotices, true), [getAdminNotices])

  useEffect(() => {
    if (hasLoadedInitialData.current) {
      return
    }

    hasLoadedInitialData.current = true

    getAdminNotices(setNotices)
    getRecipients(setRecipients)
  }, [getAdminNotices, getRecipients])

  const handleDelete = (notice: PlatformNotice) => {
    setNewConfirmation(t("delete_title"), t("delete_description", { title: notice.title }), () =>
      deleteAdminNotice(notice.id, refresh),
    )
  }

  return (
    <div className="w-full overflow-auto bg-white h-100vh p-1 md:p-0">
      <List
        messageCount={t("admin_list_title")}
        messageEmpty={t("admin_empty")}
        nameFindSearch={t("admin_search")}
        nameDropdown={statusFilters}
        defaultStatusFilter={statusFilters[2]}
        data={notices.map((notice, index) => {
          const audienceLabel = selectedAudienceTypes.has(notice.audienceType)
            ? t("target_count", { count: notice.targetCount })
            : t(`audience.${notice.audienceType}`)

          return {
            id: notice.id,
            index,
            title: notice.title,
            linkImage: "",
            isDisable: !notice.isActive,
            dataFilter: [
              notice.title,
              notice.message,
              t(`category.${notice.categoryType}`),
              t(`priority.${notice.priorityType}`),
              audienceLabel,
              notice.isActive ? t("status_active") : t("status_inactive"),
              formatDateTime(notice.publishAtUtc),
              formatDateTime(notice.expiresAtUtc),
            ],
            children: (
              <AdminListCard
                title={notice.title}
                subtitle={formatDateTime(notice.publishAtUtc)}
                badge={notice.isActive ? t("status_active") : t("status_inactive")}
                badgeClass={notice.isActive ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-700"}
                cardClass={
                  notice.isActive
                    ? "rounded-xl border-2 border-orange-200 bg-orange-50/40 p-3"
                    : "rounded-xl border-2 border-slate-200 bg-slate-50 p-3"
                }
              >
                <p className="line-clamp-3 text-sm font-semibold text-slate-600">{notice.message}</p>

                <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                  <AdminListMetricItem label={t("field_category")} value={t(`category.${notice.categoryType}`)} />
                  <AdminListMetricItem label={t("field_priority")} value={t(`priority.${notice.priorityType}`)} />
                  <AdminListMetricItem label={t("field_audience")} value={audienceLabel} />
                  <AdminListMetricItem label={t("read_count_label")} value={notice.readCount} />
                  <AdminListMetricItem label={t("field_expires_date")} value={formatDateTime(notice.expiresAtUtc)} />
                </div>

                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation()
                      handleDelete(notice)
                    }}
                    className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs font-black text-red-700 transition hover:bg-red-100"
                    data-interaction-description-key="interactionDescriptions:pages.adminDashboard.platformNotices.button.delete"
                  >
                    {t("delete")}
                  </button>
                </div>
              </AdminListCard>
            ),
          }
        })}
        handleAddButton={() => setEditingNotice(new PlatformNotice())}
        handleRefreshButton={refresh}
        handleOnClick={(id) => {
          const notice = notices.find((item) => item.id === id)

          if (notice) setEditingNotice(notice)
        }}
      />

      {editingNotice && (
        <PlatformNoticePopup
          notice={editingNotice}
          recipients={recipients}
          onClose={() => setEditingNotice(null)}
          onSuccess={refresh}
        />
      )}
    </div>
  )
}
